import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, AlertCircle, FileText, Calendar, CreditCard, RefreshCw, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import axios from "axios";
import { toast } from "sonner";
import { AgentPaymentButton, isAgentLoggedIn } from "@/components/AgentPaymentFlow";
import { PaymentDisabledBanner } from "@/components/PaymentGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DigiFlazzBills() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const customerId = searchParams.get("id");
  
  const [billData, setBillData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const adminFee = 1500;

  useEffect(() => {
    if (!customerId) {
      navigate("/");
      return;
    }
    fetchBillFromDigiFlazz();
  }, [customerId]);

  const fetchBillFromDigiFlazz = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.post(`${API}/digiflazz/inquiry`, {
        customer_no: customerId,
        buyer_sku_code: "PDAMKAB_TEBO"
      });
      setBillData(response.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Gagal mengambil data tagihan";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handlePayment = () => {
    if (!billData) return;
    // Navigate to normal payment page (Bank Transfer) with bill data from DigiFlazz
    navigate(`/payment?customer_no=${customerId}&nama=${encodeURIComponent(billData.customer_name || '')}&total=${billData.selling_price}&source=digiflazz`);
  };

  return (
    <div className="min-h-screen hero-gradient">
      <header className="container mx-auto px-6 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-heading font-semibold text-slate-800">Produk Digital</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Kembali
            </Button>
          </Link>
        </nav>
      </header>

      <main className="container mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-3xl mx-auto">
            <PaymentDisabledBanner />
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mb-2">
                Tagihan PDAM
              </h1>
              <p className="text-slate-600">
                ID Pelanggan: <span className="font-semibold text-sky-600">{customerId}</span>
              </p>
            </div>

            {isLoading ? (
              <div className="space-y-4">
                <Card className="glass-card-solid">
                  <CardContent className="p-6">
                    <Skeleton className="h-6 w-48 mb-4" />
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-2/3 mb-4" />
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              </div>
            ) : error ? (
              <div className="space-y-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
                
                <div className="glass-card rounded-2xl p-8 text-center">
                  <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                  <h2 className="text-xl font-heading font-semibold text-slate-800 mb-2">
                    Gagal Mengambil Data
                  </h2>
                  <p className="text-slate-600 mb-6">{error}</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button onClick={fetchBillFromDigiFlazz} className="rounded-full gap-2">
                      <RefreshCw className="w-4 h-4" />
                      Coba Lagi
                    </Button>
                    <Link to={`/bills?id=${customerId}`}>
                      <Button variant="outline" className="rounded-full">
                        Cek dari Database Lokal
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ) : billData ? (
              <>
                {/* Customer Info Card */}
                <Card className="glass-card-solid mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="text-sm text-slate-500">Nama Pelanggan</p>
                        <p className="font-heading font-semibold text-slate-800 text-lg">
                          {billData.customer_name || "-"}
                        </p>
                      </div>
                      <span className="px-3 py-1 rounded-full bg-sky-100 text-sky-700 text-sm font-medium">
                        {billData.lembar_tagihan || 1} Lembar
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">No. Pelanggan</p>
                        <p className="font-medium text-slate-800">{billData.customer_no}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Tarif</p>
                        <p className="font-medium text-slate-800">{billData.tarif || "-"}</p>
                      </div>
                      {billData.alamat && (
                        <div className="col-span-2">
                          <p className="text-slate-500">Alamat</p>
                          <p className="font-medium text-slate-800">{billData.alamat}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Bill Details */}
                {billData.detail && billData.detail.length > 0 ? (
                  <div className="space-y-3 mb-6">
                    <h3 className="font-heading font-semibold text-slate-800">Detail Tagihan</h3>
                    {billData.detail.map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                      >
                        <Card className="bill-card">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                <span className="font-medium text-slate-800">{item.periode}</span>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Tagihan:</span>
                                <span className="font-medium">{formatCurrency(item.nilai_tagihan)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Admin:</span>
                                <span className="font-medium">{formatCurrency(item.admin)}</span>
                              </div>
                              {item.denda > 0 && (
                                <div className="flex justify-between col-span-2">
                                  <span className="text-slate-500">Denda:</span>
                                  <span className="font-medium text-red-600">{formatCurrency(item.denda)}</span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <Card className="glass-card-solid mb-6">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-slate-800">{billData.periode || "Periode"}</span>
                        </div>
                        <span className="font-semibold text-sky-600">{formatCurrency(billData.price)}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Payment Summary */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <Card className="glass-card border-2 border-sky-200">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-sky-600" />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-slate-800">
                            Total Pembayaran
                          </h3>
                          <p className="text-sm text-slate-500">
                            Termasuk biaya admin
                          </p>
                        </div>
                      </div>

                      <div className="bg-slate-50 rounded-xl p-4 mb-4">
                        <div className="space-y-2 text-sm mb-3">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Tagihan PDAM</span>
                            <span className="font-medium">{formatCurrency(billData.price)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Biaya Admin</span>
                            <span className="font-medium">{formatCurrency(billData.admin)}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center pt-3 border-t border-slate-200">
                          <span className="font-semibold text-slate-800">Total Bayar</span>
                          <span className="text-2xl font-heading font-bold text-sky-600">
                            {formatCurrency(billData.selling_price)}
                          </span>
                        </div>
                      </div>

                      {!isAgentLoggedIn() && (
                        <>
                          <Alert className="mb-4 bg-sky-50 border-sky-200">
                            <Info className="h-4 w-4 text-sky-600" />
                            <AlertDescription className="text-sky-800 text-sm">
                              Pembayaran dilakukan via Transfer Bank. Setelah transfer, upload bukti pembayaran.
                            </AlertDescription>
                          </Alert>

                          <Button
                            onClick={handlePayment}
                            className="w-full h-14 rounded-full text-lg font-semibold btn-flow shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                            data-testid="pay-btn"
                          >
                            Lanjut Bayar
                          </Button>
                        </>
                      )}

                      {isAgentLoggedIn() && billData && (
                        <AgentPaymentButton
                          serviceType="pdam"
                          customerNo={customerId}
                          customerName={billData.customer_name || ""}
                          productName="Tagihan PDAM Real-time"
                          amount={billData.selling_price || 0}
                          adminFee={adminFee}
                          buyerSkuCode="PDAMKAB_TEBO"
                          billDetails={billData.detail ? billData.detail.map(d => ({
                            bulan: d.periode || "",
                            harga_air: d.nilai_tagihan || 0,
                            adm: d.admin || 0,
                            denda: d.denda || 0,
                            admin_fee: Math.floor(adminFee / (billData.detail.length || 1)),
                            total: (d.nilai_tagihan || 0) + (d.admin || 0) + (d.denda || 0) + Math.floor(adminFee / (billData.detail.length || 1))
                          })) : []}
                          onSuccess={() => { toast.success("Pembayaran berhasil!"); }}
                          onReceiptClose={() => { 
                            // Refresh bill data after receipt closed
                            fetchBillFromDigiFlazz();
                          }}
                        />
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </>
            ) : (
              <div className="glass-card rounded-2xl p-12 text-center">
                <FileText className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                <h2 className="text-xl font-heading font-semibold text-slate-800 mb-2">
                  Tidak Ada Tagihan
                </h2>
                <p className="text-slate-600 mb-6">
                  Tidak ada tagihan yang ditemukan untuk ID ini.
                </p>
                <Link to="/">
                  <Button className="rounded-full btn-flow">
                    Kembali ke Beranda
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
